import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import TwoFactorSetup from '../components/Auth/TwoFactorSetup';
import { FiShield, FiLink, FiLock, FiMail } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../api';
import toast from 'react-hot-toast';

function ProviderSettings() {
  const { user } = useAuth();

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Provider URL</label>
          <input
            type="url"
            value={user?.provider_url || ''}
            disabled
            className="flex h-10 w-full rounded-md border border-input bg-muted/50 px-3 py-2 text-sm text-muted-foreground cursor-not-allowed"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Provider Name</label>
          <input
            type="text"
            value={user?.provider_name || ''}
            disabled
            className="flex h-10 w-full rounded-md border border-input bg-muted/50 px-3 py-2 text-sm text-muted-foreground cursor-not-allowed"
          />
        </div>
      </div>
    </div>
  );
}

function ChangePassword() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      return toast.error('New passwords do not match');
    }
    setSaving(true);
    try {
      await authApi.changePassword({ currentPassword, newPassword });
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50";

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Current Password</label>
        <input type="password" required value={currentPassword} onChange={e => setCurrentPassword(e.target.value)}
          className={inputClass} placeholder="Enter current password" />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">New Password</label>
        <input type="password" required value={newPassword} onChange={e => setNewPassword(e.target.value)}
          className={inputClass} placeholder="Min 8 characters" minLength={8} />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Confirm New Password</label>
        <input type="password" required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
          className={inputClass} placeholder="Re-enter new password" minLength={8} />
      </div>
      <button type="submit" disabled={saving}
        className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2">
        {saving ? 'Changing...' : 'Change Password'}
      </button>
    </form>
  );
}

function ChangeEmail() {
  const { user, fetchUser } = useAuth();
  const [password, setPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await authApi.changeEmail({ password, newEmail });
      toast.success('Email changed successfully');
      setPassword('');
      setNewEmail('');
      fetchUser();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to change email');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50";

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Current Email</label>
        <input type="email" value={user?.email || ''} disabled
          className="flex h-10 w-full rounded-md border border-input bg-muted/50 px-3 py-2 text-sm text-muted-foreground cursor-not-allowed" />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">New Email</label>
        <input type="email" required value={newEmail} onChange={e => setNewEmail(e.target.value)}
          className={inputClass} placeholder="new@email.com" />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Confirm Password</label>
        <input type="password" required value={password} onChange={e => setPassword(e.target.value)}
          className={inputClass} placeholder="Enter your password to confirm" />
      </div>
      <button type="submit" disabled={saving}
        className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2">
        {saving ? 'Changing...' : 'Change Email'}
      </button>
    </form>
  );
}

export default function SettingsPage() {
  return (
    <DashboardLayout>
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">Settings</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage your account and preferences</p>
      </div>

      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold tracking-tight text-foreground flex items-center gap-2 mb-4">
          <FiLink className="text-muted-foreground" /> Provider Settings
        </h2>
        <ProviderSettings />
      </div>

      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold tracking-tight text-foreground flex items-center gap-2 mb-4">
          <FiLock className="text-muted-foreground" /> Change Password
        </h2>
        <ChangePassword />
      </div>

      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold tracking-tight text-foreground flex items-center gap-2 mb-4">
          <FiMail className="text-muted-foreground" /> Change Email
        </h2>
        <ChangeEmail />
      </div>

      <div className="card p-6 mb-6">
        <h2 className="text-lg font-semibold tracking-tight text-foreground flex items-center gap-2 mb-4">
          <FiShield className="text-muted-foreground" /> Two-Factor Authentication
        </h2>
        <TwoFactorSetup />
      </div>
    </DashboardLayout>
  );
}
